/**
 * @file Manages showing, hiding, and setting up UI pages.
 * @module PageManager
 */
import { buildGameConfig} from '../lib/utils.js';
import * as templates from './viewTemplates.js';
import { getEnableAnimalsToggleHtml, getNpcWanderingToggleHtml, getWeatherEffectsToggleHtml, getAmbientAnimalsToggleHtml, get3dEffectsToggleHtml, getColorblindModeToggleHtml  } from './viewTemplates.js';
import { featuresConfig } from '../config/featuresConfig.js';
import { GAME_TYPE_QUICK_CLICK, GAME_TYPE_CAMPAIGN_CLICK } from '../constants/gameTypes.js';

export class PageManager {
    constructor(dependencies) {
        this.dom = dependencies.dom;
        this.playAudio = dependencies.playAudio;
        this.eventEmitter = dependencies.eventEmitter;
        this.configManager = dependencies.configManager;
        this.biomeManager = dependencies.biomeManager;
        this.saveManager = dependencies.saveManager;
        this.showPrompt = dependencies.showPrompt;
        this.achievementsConfig = dependencies.achievementsConfig;
        this.showAchievementToast = dependencies.showAchievementToast;
        this.textConfig = dependencies.textConfig;
        this.masterItemsConfig = dependencies.masterItemsConfig;
        this.baseConfig = dependencies.baseConfig;
        this.gameOptions = dependencies.gameOptions;
        this.playerStatsManager = dependencies.playerStatsManager;
        this.preferencesManager = dependencies.preferencesManager;
        this.createCollector = dependencies.createCollector;
        this.gridSize = dependencies.gridSize;
        this._collectorNeedsCreation = dependencies._collectorNeedsCreation;
        this.showAboutPage = this.showAboutPage.bind(this);
        this.quickGameSelectedSettings = dependencies.quickGameSelectedSettings;
        this.campaignSelectedSettings = dependencies.campaignSelectedSettings;
    }




    /**
     * Gathers achievement data and displays the achievements page.
     */
    showAchievementsPage() {
        const unlockedKeys = this.playerStatsManager.getUnlockedAchievements();
        const progressData = this.playerStatsManager.getAchievementProgressObject();
        this.displayAchievementsPage(this.achievementsConfig, unlockedKeys, progressData);
    }

    // --- Page Setup & Content Population ---

    /**
     * Sets up the click listener for the achievements page to handle replaying effects.
     * @private
     */
    _setupAchievementsPageListener() {
        const listContainer = this.dom.pages.achievements.querySelector('#achievements-list');
        if (listContainer && !listContainer.dataset.listenerAttached) {
            listContainer.addEventListener('click', this._handleAchievementClick.bind(this));
            listContainer.dataset.listenerAttached = 'true';
        }
    }

    /**
     * Handles clicks within the achievements list, replaying effects for unlocked items.
     * @param {MouseEvent} event - The click event.
     * @private
     */
    _handleAchievementClick(event) {
        const achievementElement = event.target.closest('.unlocked-achievement');
        if (achievementElement) {
            const achievementKey = achievementElement.dataset.achievementKey;
            const achievement = this.achievementsConfig[achievementKey];
            if (achievement) {
                this.playAudio('achievement');
                this.showAchievementToast(achievement);
            }
        }
    }

    /**
     * Sets up the guide page, loading content and populating dynamic data.
     * @param {object} biomes - The biomes configuration object.
     * @param {object} baseConfig - The base game configuration.
     * @param {object} masterItemsConfig - The master items configuration.
     * @param {Array<object>} campaignLevels - An array of campaign level objects.
     * @param {object} gameOptions - The game options configuration object.
     * @returns {Promise<void>} A promise that resolves when the guide page is set up.
     */
    async setupGuidePage(biomes, baseConfig, masterItemsConfig, campaignLevels, gameOptions) {
        const pageContainer = await this._loadPageContent('guide', 'pages/guide-content.html');
        if (!pageContainer) return;

        if (!pageContainer.dataset.listenerAttached) {
            const backButton = pageContainer.querySelector('#guide-back-to-game-btn');
            backButton.addEventListener('click', () => {
                // Re-apply the actual game's biome when leaving the guide
                this.biomeManager.applyBiome(this.biomeManager.getCurrentBiomeKey());
                this.showPage('game');
            });
            pageContainer.dataset.listenerAttached = 'true';
        }

        const contentContainer = pageContainer.querySelector('#guide-content');
        if (!contentContainer) return;

        // Get the currently active biome key to set the initial state of the guide
        let viewingThemeKey = this.biomeManager.getCurrentBiomeKey();
        // Apply the theme for the current biome so the guide page matches
        this.biomeManager.applyBiome(viewingThemeKey);

        // Get the default path for the current biome, falling back to 'default' if not found
        let viewingPathLength = biomes[viewingThemeKey]?.defaultPathKey || 'default';

        const refresh = () => {
            const availablePathLengths = biomes[viewingThemeKey].upgradePathLinear;
            const gameConfigForGuide = buildGameConfig({ masterItemsConfig, baseConfig, biomesConfig: biomes, biomeKey: viewingThemeKey, pathLength: viewingPathLength, gridSize: 1 });
            
            const flatMasterItems = Object.values(masterItemsConfig).reduce((acc, category) => {
                return { ...acc, ...category };
            }, {});
            
            const featuresHtml = `
                <hr class="my-8 border-gray-600">
                <h2 class="text-2xl font-bold">Game Features & Ideas</h2>
                <div class="space-y-6 mt-4">
                    ${featuresConfig.map(section => `
                        <div>
                            <h3 class="text-xl font-semibold text-orange-400">${this._getStatusIcon(section.status)} ${section.title}</h3>
                            <ul class="list-disc list-inside ml-4 mt-2 text-gray-300 space-y-2">
                                ${section.items.map(item => {
        const subItemsHtml = item.subItems ? `<ul class="list-disc list-inside ml-6 mt-1 text-gray-400">${item.subItems.map(subItem => `<li>${this._getStatusIcon(subItem.status)} ${subItem.text}</li>`).join('')}</ul>` : '';
        return `<li>${this._getStatusIcon(item.status)} ${item.text}${subItemsHtml}</li>`;
    }).join('')}
                            </ul>
                        </div>
                    `).join('')}
                </div>
            `;

            contentContainer.innerHTML = `
                ${templates.getGuideRulesHtml(this.textConfig)}

                <h3>${this.textConfig.guide.biomesTitle}</h3>
                <p class="mb-2 text-gray-400">${this.textConfig.guide.biomesDescription}</p>
                ${templates.getBiomesHtml(biomes, viewingThemeKey)}

                <h3>${this.textConfig.guide.pathLengthTitle}</h3>
                <p class="mb-2 text-gray-400">${this.textConfig.guide.pathLengthDescription}</p>
                ${templates.getPathLengthOptionsHtml(availablePathLengths, viewingPathLength)}

                <p class="mt-4 mb-2 text-gray-400">${this.textConfig.guide.upgradePathDescription}</p>
                ${templates.getGameItemsHtml(gameConfigForGuide)}

                ${(campaignLevels && gameOptions) ? templates.getCampaignProgressionVisualisationHtml(campaignLevels, biomes, gameOptions, flatMasterItems, this.textConfig) : ''}

                ${featuresHtml}
            `;
        };        // Attach event listeners for biome and path length selection if not already attached
        

        if (!contentContainer.dataset.listenerAttached) {
            contentContainer.addEventListener('click', (e) => {
                let needsRefresh = false;
                if (e.target.matches('.biome-selector')) {
                    viewingThemeKey = e.target.dataset.biome;
                    this.biomeManager.applyBiome(viewingThemeKey); // Apply theme for illustration
                    // When a new biome is selected, reset the path length to its default
                    viewingPathLength = biomes[viewingThemeKey]?.defaultPathKey || 'default';
                    needsRefresh = true;
                }
                if (e.target.matches('.path-length-selector')) {
                    viewingPathLength = e.target.dataset.length;
                    needsRefresh = true;
                }
                if (needsRefresh) refresh();
            });
            contentContainer.dataset.listenerAttached = 'true';
        }

        refresh();
    }

    /**
     * Returns a status icon based on the provided status string.
     * @param {string} status - The status ('done', 'in-progress', 'todo').
     * @returns {string} The corresponding emoji icon.
     * @private
     */
    _getStatusIcon(status) {
        switch (status) {
            case 'done': return '✅';
            case 'in-progress': return '🟡';
            case 'todo': return '❌';
            default: return '';
        }
    }
    /**
     * Displays the about page.
     */
    showAboutPage() {
        this.showPage('about');
    }
    /**
     * Sets up the about page, loading content and handling interactions.
     * @returns {Promise<void>} A promise that resolves when the about page is set up.
     */
    async setupAboutPage() {
        const container = await this._loadPageContent('about', 'pages/about-content.html');
        if (container && !container.dataset.listenerAttached) {
            const backButton = container.querySelector('#about-back-to-main-btn');
            if (backButton) backButton.addEventListener('click', () => this.showPage('intro'));
            container.dataset.listenerAttached = 'true';
        }
    }


    /**
     * Fetches and injects HTML content for a page if it's not already loaded.
     * @param {string} pageKey - The key of the page in the DOM map.
     * @param {string} contentUrl - The URL of the HTML content to fetch.
     * @returns {Promise<HTMLElement|null>} The container element or null on failure.
     * @private
     */
    async _loadPageContent(pageKey, contentUrl) {
        const container = this.dom.pages[pageKey];
        if (!container) {
            console.error(`UI Error: Page container for '${pageKey}' not found!`);
            return null;
        }
        if (container.innerHTML.trim() !== '') {
            return container; // Content already loaded
        }
        try {
            const response = await fetch(contentUrl);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            container.innerHTML = await response.text();
            return container;
        } catch (error) {
            console.error(`Failed to load content for ${pageKey}: ${error.message}`);
            container.innerHTML = '<p class="text-red-500 text-center">Error: Could not load content.</p>';
            return null;
        }
    }

    /**
     * Displays the achievements page.
     * @param {object} allAchievements - The configuration object for all achievements.
     * @param {Set<string>} unlockedKeys - A Set containing the keys of unlocked achievements.
     * @param {Object.<string, number>} progressData - An object mapping achievement keys to their current count.
     */
    displayAchievementsPage(allAchievements, unlockedKeys, progressData) {
        const pageContainer = this.dom.pages.achievements;
        if (!pageContainer) return;

        const listContainer = pageContainer.querySelector('#achievements-list');
        if (listContainer) {
            listContainer.innerHTML = templates.getAchievementsHtml(allAchievements, unlockedKeys, progressData);
        }

        // Set up the listener to handle clicks on the newly rendered achievements.
        this._setupAchievementsPageListener();

        this.showPage('achievements');
    }

    /**
     * Shows a specific UI page and hides all others.
     * @param {string} pageKey - The key of the page to show (e.g., 'game', 'settings').
     */
    showPage(pageKey) {
        Object.values(this.dom.pages).forEach(page => page.classList.add('hidden'));
        this.dom.pages[pageKey].classList.remove('hidden');
        this.playAudio('click');

        if (pageKey === 'game' && this._collectorNeedsCreation) {
            this.createCollector(this.gridSize);
            this._collectorNeedsCreation = false;
        }
        this.eventEmitter.emit('ui:pageChanged', { pageKey });
    }

}